import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "@/components/wpl/button/src/button";
import { TextInput } from "@/components/wpl/text-input/src/text-input";

import { useLocation } from "../../utils/location-provider";

const inputContainerStyle = css({
    display: "flex",
    alignItems: "flex-start",
    width: "100%"
});

const errorStyles = css({
    /* eslint-disable-next-line local/ensure-wac-token-usage */
    color: "#de350b",
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "1.2",
    margin: 0,
    marginBlockStart: "4px"
});

const inputStyle = css({
    flex: 1
});

const searchInputContainerStyle = css({
    display: "inline-block",
    textAlign: "left",
    width: "100%"
});

const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

type ConsentConfig = {
    hasUserMadeChoiceOnMarketingCommunicationOptIn: boolean;
    locale: string;
    localeRequiresMarketingCommunicationOptIn: boolean;
    usersChoiceOnMarketingCommunicationOptIn: boolean;
};

export const SourcetreeAppMailingListSignup = () => {
    const location = useLocation();
    const intl = useIntl();

    const emptyEmailError = intl.formatMessage({
        id: "SourcetreeAppFooter.top.emptyEmailError",
        defaultMessage: "This field is required"
    });

    const invalidEmailError = intl.formatMessage({
        id: "SourcetreeAppFooter.top.invalidEmailError",
        defaultMessage: "This field requires a valid email address"
    });

    const [errorMessage, setErrorMessage] = useState("");
    const [isValidatingEmail] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [honeypotInput, setHoneypotInput] = useState("");
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [consentConfig, setConsentConfig] = useState<ConsentConfig>();

    useEffect(() => {
        if (isValidatingEmail) {
            if (emailInput === "") {
                setErrorMessage(emptyEmailError);
            } else {
                validateEmail(emailInput)
                    ? setErrorMessage("")
                    : setErrorMessage(invalidEmailError);
            }
        }
    }, [isValidatingEmail, emailInput, emptyEmailError, invalidEmailError]);

    useEffect(() => {
        getConsentConfig();
    }, []);

    const getConsentConfig = async () => {
        const stargateUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
        const consentConfigUrl = `${stargateUrl}/preferencesV2/consent-config`;

        await fetch(consentConfigUrl, {
            headers: {
                "Accept": "application/json",
                "Content-type": "application/json; charset=UTF-8"
            },
            method: "POST"
        })
            .then((response) => response.json())
            .then((data) => setConsentConfig(data))
            .catch(() => {
                console.error("Failed to fetch consent config");
            });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (emailInput === "") {
            setErrorMessage(emptyEmailError);
            return;
        }

        if (!validateEmail(emailInput)) {
            return;
        }

        if (honeypotInput) return;

        const stargateUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
        const postUrl = `${stargateUrl}/preferencesV2/update`;

        // These are hardcoded values that are required by the endpoint.
        // https://bitbucket.org/atlassian/magnolia-resources/annotate/f65b7a5bf31677bcde32caa3305364c7ffb88808/resources/js/components/generic/exact-target-formV2.ts?at=qa
        await fetch(postUrl, {
            headers: {
                "Accept": "application/json",
                "Content-type": "application/json; charset=UTF-8"
            },
            method: "POST",
            body: JSON.stringify({
                consentCheckboxValue: true,
                consentLabelText: "",
                email: emailInput,
                formId: "2ed5d5c4-fe00-4259-a4e0-e82e997a371b",
                formUrl: location.href,
                locale: consentConfig?.locale || "UNKNOWN",
                privacyPolicyText: "",
                site: "atlassian"
            })
        })
            .then(() => setSignupSuccess(true))
            .catch(() => {
                setErrorMessage(
                    intl.formatMessage({
                        id: "SourcetreeApp.top.signupError",
                        defaultMessage: "There was a server error submitting."
                    })
                );
            });
    };

    return (
        <SourcetreeAppMailingListSignupView
            errorMessage={errorMessage}
            emailInput={emailInput}
            setEmailInput={setEmailInput}
            setHoneypotInput={setHoneypotInput}
            signupSuccess={signupSuccess}
            handleSubmit={handleSubmit}
        />
    );
};

interface SourcetreeAppMailingListSignupViewProps {
    errorMessage: string;
    emailInput: string;
    setEmailInput: Dispatch<SetStateAction<string>>;
    setHoneypotInput: Dispatch<SetStateAction<string>>;
    signupSuccess: boolean;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

const buttonStyle = css({
    marginInlineStart: "16px",
    marginBlockStart: "4px"
});

export const SourcetreeAppMailingListSignupView = (
    props: SourcetreeAppMailingListSignupViewProps
) => {
    const {
        errorMessage,
        emailInput,
        setEmailInput,
        setHoneypotInput,
        signupSuccess,
        handleSubmit
    } = props;

    const intl = useIntl();

    const subscribeText = intl.formatMessage({
        id: "SourcetreeAppFooter.top.subscribe",
        defaultMessage: "Subscribe"
    });

    return (
        <div>
            {!signupSuccess && (
                <form onSubmit={handleSubmit}>
                    <div css={inputContainerStyle}>
                        <div css={searchInputContainerStyle}>
                            <TextInput
                                name="email"
                                css={inputStyle}
                                value={emailInput}
                                id="email"
                                testId="email"
                                label=""
                                placeholder="email@example.com"
                                onChangeHandler={setEmailInput}
                                validation={(value) => {
                                    if (value === "") {
                                        return [
                                            false,
                                            intl.formatMessage({
                                                id: "SourcetreeAppFooter.top.emptyEmailError",
                                                defaultMessage:
                                                    "This field is required"
                                            })
                                        ];
                                    } else if (!validateEmail(value)) {
                                        return [
                                            false,
                                            intl.formatMessage({
                                                id: "SourcetreeAppFooter.top.invalidEmailError",
                                                defaultMessage:
                                                    "This field requires a valid email address"
                                            })
                                        ];
                                    }
                                    return [true, ""];
                                }}
                            />
                            {errorMessage && (
                                <div css={errorStyles}>{errorMessage}</div>
                            )}
                        </div>

                        {/* honeypot */}
                        <div css={css({ display: "none" })}>
                            <input
                                type="text"
                                name="list name"
                                aria-label="keep this field blank"
                                onChange={(e) =>
                                    setHoneypotInput(e.target.value)
                                }
                            />
                        </div>
                        <Button
                            type={"secondary"}
                            text={subscribeText}
                            inputType={"submit"}
                            dataEventProps={{
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-product-key": "sourcetreeapp",
                                "data-event-container": "button"
                            }}
                            size={"large"}
                            color={"whiteblue"}
                            css={buttonStyle}
                            aria-label={subscribeText}
                        />
                    </div>
                </form>
            )}
            {signupSuccess && (
                <p>
                    <FormattedMessage
                        id="SourcetreeApp.top.signup-success"
                        defaultMessage="Thanks for signing up!"
                    />
                </p>
            )}
        </div>
    );
};

export default SourcetreeAppMailingListSignup;
